import * as React from 'react';

import { configureAutoTrack, record as analyticsRecord } from 'aws-amplify/analytics';
import dayjs from 'dayjs';
import { debounce } from 'lodash';

import { AnalyticsContext } from '.';
import { useAppSelector } from '../../redux/hooks';
import { AnalyticsContextProps } from './types';

const ANALYTIC_KEY = 'app-analytics-attributes';

const AnalyticsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const venueState = useAppSelector((state) => state.venue.value);

  React.useEffect(() => {
    configureAutoTrack({
      enable: true,
      type: 'event',
      options: {
        events: ['click'],
        selectorPrefix: 'data-amplify-analytics-',
      },
    });
  }, []);

  const attributes = React.useMemo<Record<string, string> | undefined>(
    () =>
      venueState
        ? {
            entity: 'venue',
            venueId: venueState?.id,
            appVersion: 'local',
            locale: dayjs().locale(),
            platform: 'Web',
            model: 'model',
            modelVersion: 'modelVersion',
            platformVersion: 'platformVersion',
            timezone: dayjs.tz.guess(),
            latitude: String(venueState.location?.lat),
            longitude: String(venueState.location?.lon),
            marketID: venueState.marketID || '',
          }
        : undefined,
    [venueState],
  );

  React.useEffect(() => {
    if (attributes) {
      localStorage.setItem(ANALYTIC_KEY, JSON.stringify(attributes));
    }
  }, [attributes]);

  const recordDebounce = React.useMemo(() => debounce(analyticsRecord, 500), []);

  const record = React.useCallback<AnalyticsContextProps['record']>(
    (name, additionalAttributes) => {
      if (attributes) {
        const event = {
          name,
          attributes: {
            ...attributes,
            ...additionalAttributes,
          },
        };

        recordDebounce(event);
      }
    },
    [attributes, recordDebounce],
  );

  return <AnalyticsContext.Provider value={{ record }}>{children}</AnalyticsContext.Provider>;
};

export default AnalyticsProvider;
